import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mainApi } from '@/lib/api';
import { QUERY_KEYS, StoreType } from '@/lib/constants';
import { stanNotify } from '@/lib/stanNotify';
import type { FunnelPageRouting } from '../../types';

export interface UpdateFunnelPagePayload {
    page_id: number;
    referencePageId: number;
    productTitle: string;
    order: number;
    /** true → PUT /pages (publishes); false/undefined → PUT /pages/draft (saves as draft). */
    publish?: boolean;
    topBannerText?: string;
    /** Null clears the override (falls back to store price). */
    funnelPrice?: number | null;
    funnelCtaText?: string;
    /** null explicitly clears routing; undefined leaves it untouched. */
    routing?: FunnelPageRouting | null;
}

export function useUpdateFunnelPage(storeId: number, notifyOnSuccess = false) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: UpdateFunnelPagePayload) => {
            const product: Record<string, unknown> = {
                type: 'funnel-page',
                title: payload.productTitle,
                funnel_page: { reference_page_id: payload.referencePageId },
            };

            if (typeof payload.funnelPrice === 'number') {
                product.funnel_price = payload.funnelPrice;
            }
            if (payload.funnelCtaText && payload.funnelCtaText.length > 0) {
                product.funnel_cta_text = payload.funnelCtaText;
            }

            const body: Record<string, unknown> = {
                page_id: payload.page_id,
                type: 'funnel-page',
                order: payload.order,
                product,
            };

            if (payload.topBannerText !== undefined) {
                body.top_banner_text = payload.topBannerText;
            }

            if (payload.routing !== undefined) {
                body.routing = payload.routing;
            }

            const endpoint = payload.publish ? `v1/stores/${storeId}/pages` : `v1/stores/${storeId}/pages/draft`;

            const response = await mainApi.put(endpoint, body);
            return response.data;
        },

        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FUNNELS, 'detail', storeId] }),
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL] }),
            ]);

            if (notifyOnSuccess) {
                stanNotify({ type: 'success', title: 'Page updated!', duration: 2000 });
            }
        },
        onError: () => {
            stanNotify({
                type: 'error',
                title: 'Oops!',
                text: 'Failed to update page. Please try again.',
                duration: 2000,
            });
        },
    });
}
